/**
 * Overlay-side payment host. Runs inside the widget's shadow root (embed mode)
 * where Stripe Elements can't mount, and delegates card entry to a Dorsia-origin
 * iframe (`EmbeddedPaymentFrame` at `/embed/pay`).
 *
 * Responsibilities:
 *  1. Mint a SetupIntent with the member bearer token (the token never leaves the
 *     overlay — only the client secret is handed to the iframe).
 *  2. Frame the inner page, drive the nonce-bound postMessage handshake, and size
 *     the iframe to the reported form height.
 *  3. On success, attach the returned Stripe payment method via
 *     `add-payment-method` (→ Dorsia payment_id) and continue the flow exactly as
 *     the SPA card path does (`updateWidget` + analytics + `onSuccess`).
 *
 * Unhappy paths (HOS-1821): a blocked/slow iframe (no `ready`) re-frames once with
 * a fresh nonce then falls back to the hosted booking page; frame-reported FATAL
 * errors and SetupIntent mint failures surface a Retry that re-mints + re-frames;
 * an explicit loading/attach spinner and a post-success `done` guard keep the step
 * from ever getting stuck or double-attaching.
 */
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import Config from '@/lib/Config.ts';
import { setupIntent, setPaymentMethod } from '@/lib/api.ts';
import { track, resolveBookingType } from '@/lib/analytics/index.ts';
import {
  AuthAtom,
  reloadPaymentMethodsAtom,
  useRestaurantId,
  useUserDetails,
  useWidgetState,
  widgetPaymentMethodSelection,
} from '../lib/state.ts';
import {
  EMBED_PAY_SOURCE,
  parseEmbedPayMessage,
  type EmbedPayMessage,
} from './embedPaymentMessages.ts';
import { useAnnounce } from '../lib/announcer.ts';

import styles from '../EventPage/EventPage.module.css';

const MIN_FRAME_HEIGHT = 360;
// Mirror the loader's OPEN_TIMEOUT_MS (embed.ts): how long we wait for the iframe
// to post `ready` before tearing it down.
const READY_TIMEOUT_MS = 8000;

// A per-session handshake nonce. Not a cryptographic secret — it binds a session
// alongside strict event.origin checks. Regenerated on every (re)frame.
function makeNonce(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Non-secure-context fallback.
    return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: 20,
        height: 20,
        border: '2px solid #e5e5e5',
        borderTopColor: '#0d0d0d',
        borderRadius: '50%',
        animation: 'dorsia-embed-pay-spin 0.6s linear infinite',
      }}
    />
  );
}

const SPINNER_KEYFRAMES = '@keyframes dorsia-embed-pay-spin { to { transform: rotate(360deg); } }';

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  background: 'rgba(255, 255, 255, 0.92)',
};

const overlayLabelStyle: CSSProperties = {
  fontFamily: "'Sailec', sans-serif",
  color: '#8d8d8d',
  fontSize: 15,
};

function EmbeddedPaymentHost({ onSuccess }: { onSuccess: (brand: string, last4: string) => void }) {
  const auth = useAtomValue(AuthAtom);
  const user = useUserDetails();
  const restaurantId = useRestaurantId();
  const reloadPaymentMethods = useSetAtom(reloadPaymentMethodsAtom);
  const [widget, updateWidget] = useWidgetState();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [nonce, setNonce] = useState(makeNonce);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [height, setHeight] = useState(MIN_FRAME_HEIGHT);
  // `error` is a recoverable inline banner (e.g. an attach failure). `fatalError`
  // is a terminal state that replaces the iframe with a Retry (re-mint + re-frame).
  const [error, setError] = useState<string | undefined>();
  const [fatalError, setFatalError] = useState<string | undefined>();
  // Handshake / lifecycle state (Task 5 explicit states + Task 1 ready-timeout).
  const [ready, setReady] = useState(false);
  const [attempt, setAttempt] = useState(0); // 0 = first frame, 1 = the one retry
  const [fallback, setFallback] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [mintKey, setMintKey] = useState(0); // bump to re-run the mint effect
  const attachingRef = useRef(false);
  // Set once a `success` is accepted so a replayed `success` can't re-run attach.
  const doneRef = useRef(false);

  // Announce payment errors to assistive tech (no-op outside the overlay). The
  // inline error banner / fatal Retry are visual; this voices them too.
  const announce = useAnnounce();
  useEffect(() => {
    const msg = fatalError ?? error;
    if (msg) announce(msg);
  }, [error, fatalError, announce]);

  const bookingOrigin = useMemo(() => new URL(Config.data.uri.booking).origin, []);
  const frameSrc = useMemo(() => {
    const url = new URL('/embed/pay', bookingOrigin);
    url.searchParams.set('parentOrigin', window.location.origin);
    url.searchParams.set('nonce', nonce);
    return url.toString();
  }, [bookingOrigin, nonce]);
  // Hosted booking page for the fallback panel (restaurant id from widget state).
  const fallbackHref = useMemo(
    () =>
      new URL(
        restaurantId != null ? `/restaurant/${restaurantId}/booking` : '/',
        bookingOrigin
      ).toString(),
    [bookingOrigin, restaurantId]
  );

  // Mint the SetupIntent up front (the token stays here; only the secret ships).
  // Re-runs when `mintKey` bumps (Retry after a mint failure — Task 3).
  useEffect(() => {
    if (!auth?.token) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await setupIntent(auth.token);
        if (cancelled) return;
        if (!response?.setupIntent?.client_secret) {
          Sentry.captureMessage('Widget embed: setup-intent returned no client_secret');
          // test hook: see HOS-1822 (block setup-intent to force this Retry path)
          setFatalError('Sorry, there was an issue starting payment. Please try again.');
          return;
        }
        setClientSecret(response.setupIntent.client_secret);
      } catch (e) {
        if (cancelled) return;
        Sentry.captureException(e);
        // test hook: see HOS-1822 (block setup-intent to force this Retry path)
        setFatalError('Sorry, there was an issue starting payment. Please try again.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth?.token, mintKey]);

  const postToFrame = useCallback((message: EmbedPayMessage) => {
    iframeRef.current?.contentWindow?.postMessage(message, bookingOrigin);
  }, [bookingOrigin]);

  const completeAttach = useCallback(
    async (paymentMethodId: string, brand: string, last4: string) => {
      // `attachingRef` blocks a concurrent double-attach; `doneRef` blocks any
      // re-attach after a success has already been accepted.
      if (attachingRef.current || doneRef.current || !auth?.token) return;
      attachingRef.current = true;
      doneRef.current = true;
      setAttaching(true);
      try {
        const dorsia_payment = await setPaymentMethod(auth.token, { paymentMethodId });
        updateWidget(widgetPaymentMethodSelection(dorsia_payment.payment_id));

        if (restaurantId != null) {
          track('Widget Payment Method Added', {
            restaurant_id: restaurantId,
            booking_type: resolveBookingType(widget.bookingType),
            payment_type: 'card',
          });
        }

        reloadPaymentMethods();
        onSuccess(brand, last4);
        // Success is terminal — the overlay advances; keep the spinner up.
      } catch (e) {
        Sentry.captureException(e);
        setError('Sorry, there was an issue saving your card. Please try again.');
        // Re-open so a genuine fresh `success` could still complete the attach.
        attachingRef.current = false;
        doneRef.current = false;
        setAttaching(false);
      }
    },
    [auth?.token, restaurantId, widget.bookingType, updateWidget, reloadPaymentMethods, onSuccess]
  );

  // Full reset: re-mint the SetupIntent AND re-frame with a fresh nonce. Backs the
  // Retry on both frame-reported FATAL errors (Task 2) and mint failures (Task 3).
  const restart = useCallback(() => {
    attachingRef.current = false;
    doneRef.current = false;
    setError(undefined);
    setFatalError(undefined);
    setFallback(false);
    setReady(false);
    setAttaching(false);
    setAttempt(0);
    setHeight(MIN_FRAME_HEIGHT);
    setClientSecret(null);
    setNonce(makeNonce());
    setMintKey((k) => k + 1);
  }, []);

  // Handle messages from the iframe: ready → send init, resize, success, error.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== bookingOrigin) return;
      const msg = parseEmbedPayMessage(event.data, nonce);
      if (!msg) return;
      // Message hygiene (Task 6): once a success has been accepted, ignore every
      // later message so a replayed `success` can't re-run attach. The origin +
      // nonce checks above are unchanged.
      // test hook: see HOS-1822 (replay a `success` to exercise this guard)
      if (doneRef.current) return;

      switch (msg.type) {
        case 'ready':
          setReady(true);
          if (clientSecret) {
            postToFrame({
              source: EMBED_PAY_SOURCE,
              type: 'init',
              nonce,
              clientSecret,
              email: user?.email,
              firstName: user?.first_name,
              lastName: user?.last_name,
            });
          }
          break;
        case 'resize':
          // Clamp to a sane range so a runaway report can't blow out the overlay.
          setHeight(Math.min(800, Math.max(MIN_FRAME_HEIGHT, msg.height)));
          break;
        case 'success':
          setError(undefined);
          void completeAttach(msg.paymentMethodId, msg.brand, msg.last4);
          break;
        case 'error':
          // test hook: see HOS-1822 (block js.stripe.com or decline to exercise)
          if (msg.errorKind === 'fatal') {
            setFatalError(msg.message || 'Payment could not be loaded. Please try again.');
          } else {
            // Recoverable card issue escalated as a banner (frame keeps most inline).
            setError(msg.message || 'Card was not accepted.');
          }
          break;
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [bookingOrigin, nonce, clientSecret, user, postToFrame, completeAttach]);

  // Ready timeout (Task 1): if the iframe never posts `ready` (blocked/slow), tear
  // it down and re-frame ONCE with a fresh nonce; a second miss shows the fallback.
  useEffect(() => {
    if (ready || fallback || fatalError) return;
    // test hook: see HOS-1822 (block /embed/pay so `ready` never arrives)
    const timer = window.setTimeout(() => {
      if (attempt === 0) {
        setNonce(makeNonce());
        setAttempt(1);
      } else {
        setFallback(true);
      }
    }, READY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [ready, fallback, fatalError, attempt]);

  // If the iframe reported ready before the secret arrived, (re)send init once
  // the secret is available.
  useEffect(() => {
    if (clientSecret) {
      postToFrame({
        source: EMBED_PAY_SOURCE,
        type: 'init',
        nonce,
        clientSecret,
        email: user?.email,
        firstName: user?.first_name,
        lastName: user?.last_name,
      });
    }
  }, [clientSecret, nonce, user, postToFrame]);

  // Terminal fallback: two ready-timeouts → offer the hosted booking page. Styled
  // like the existing error UI, mirroring the loader's `showFailure` pattern.
  if (fallback) {
    return (
      <div className={styles.formSection}>
        <div className={styles.error}>
          We couldn&rsquo;t load the secure payment form.{' '}
          <a
            href={fallbackHref}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
          >
            Continue on Dorsia &rarr;
          </a>
        </div>
      </div>
    );
  }

  // Terminal-with-Retry: a frame-reported FATAL error (Task 2) or a mint failure
  // (Task 3). Retry re-mints the SetupIntent and re-frames.
  if (fatalError) {
    return (
      <div className={styles.formSection}>
        <div className={styles.error}>{fatalError}</div>
        <button className={styles.primaryButton} onClick={restart}>
          Retry
        </button>
      </div>
    );
  }

  // Loading (Task 5): explicit state until the frame is `ready` AND we've handed
  // it the SetupIntent secret (`init`). Also covers the attach spinner.
  const loading = !ready || !clientSecret;

  return (
    <div className={styles.formSection}>
      <style>{SPINNER_KEYFRAMES}</style>
      {error && <div className={styles.error}>{error}</div>}
      <div style={{ position: 'relative', width: '100%' }}>
        <iframe
          ref={iframeRef}
          key={nonce}
          src={frameSrc}
          title="Payment"
          allow="payment"
          style={{
            width: '100%',
            height,
            border: 'none',
            background: 'transparent',
            colorScheme: 'light',
          }}
        />
        {(loading || attaching) && (
          <div style={overlayStyle} role="status" aria-live="polite">
            <Spinner />
            <span style={overlayLabelStyle}>
              {attaching ? 'Saving your card…' : 'Loading secure payment…'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmbeddedPaymentHost;
